import { FunctionComponent, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";

import { Modal } from "@/components/Modals/Modal";
import { useGetScoreQuery } from "@/pages/Private/redux/score/score.api";

import { Tooltip } from "@/components/Tooltip/Tooltip";
import { CategoryReasoningPopover } from "@/components/CategoryReasoningPopover/CategoryReasoningPopover";
import { Dropdown } from "@/components/Dropdown/Dropdown";
import { TextArea } from "@/components/TextArea/TextArea";

import { useUpdateLeadScoringResultMutation } from "@/pages/Private/redux/list/list.api";

import { ListPerson } from "../../schema/list";

export const ModalChangePersonaCategory: FunctionComponent<{ listPerson: ListPerson }> = ({
	listPerson,
}) => {
	const { data: score } = useGetScoreQuery();

	const { t } = useTranslation();
	const [showModal, setShowModal] = useState(false);
	const [reason, setReason] = useState("");
	const [updateLeadScoringResult, { isLoading }] = useUpdateLeadScoringResultMutation();

	const [selectedCategory, setSelectedCategory] = useState<{
		id: number;
		title: string;
	} | null>();

	useEffect(() => {
		if (listPerson?.prospect?.scorePersonaCategoryResult?.scorePersonaCategory) {
			setSelectedCategory({
				id: listPerson?.prospect?.scorePersonaCategoryResult?.scorePersonaCategory?.id || 0,
				title:
					listPerson?.prospect?.scorePersonaCategoryResult?.scorePersonaCategory?.category || "",
			});
		}
	}, [listPerson]);

	const handleClose = () => {
		setShowModal(false);
		setReason("");
	};

	const handleSave = async () => {
		await updateLeadScoringResult({
			id: listPerson.id,
			scorePersonaCategoryId: selectedCategory?.id || 0,
			personaResult: selectedCategory?.title || "",
			reason,
		}).unwrap();

		setShowModal(false);
		setReason("");
	};

	return (
		<div
			className="flex items-center cursor-pointer"
			onClick={(e) => {
				e.preventDefault();
				e.stopPropagation();

				setShowModal(true);
			}}
		>
			<Tooltip
				content={
					<CategoryReasoningPopover
						categoryName={listPerson?.prospect?.scorePersonaCategoryResult?.result}
						kind="persona"
						prioScore={listPerson?.prospect?.scorePersonaCategoryResult?.personaScore}
						reasoning={listPerson?.prospect?.scorePersonaCategoryResult?.reason}
					/>
				}
				disabled={!listPerson?.prospect?.scorePersonaCategoryResult?.reason}
				panel
			>
				<div className="flex flex-col">
					<span className="text-sm">
						{listPerson?.prospect?.scorePersonaCategoryResult?.result || "Not categorized"}
					</span>
				</div>
			</Tooltip>
			<Modal
				handleClose={(e) => {
					e.preventDefault();
					e.stopPropagation();

					handleClose();
				}}
				handleSave={() => {
					handleSave();
					handleClose();
				}}
				disableSave={isLoading}
				isLoading={isLoading}
				isOpened={showModal}
				title={t("scoring.changePersonaCategory")}
			>
				<div>
					<div className="text-sm font-bold">
						{t("scoring.changePersonaCategoryFor", { name: listPerson?.prospect?.name })}
					</div>
					<Dropdown
						data={[
							...(score?.prospectPersonaCategories?.map((category) => ({
								id: category.id || 0,
								title: `${category.category}` || "",
							})) || []),
							{
								id: 0,
								title: "Disqualified",
							},
						]}
						defaultValue={
							selectedCategory
								? {
										id: selectedCategory.id,
										title: selectedCategory.title,
									}
								: undefined
						}
						handleSelect={(selected) => {
							setSelectedCategory({
								id: selected.id as number,
								title: selected.title,
							});
						}}
						label={t("scoring.personaCategory")}
					/>
					<div className="mt-4">
						<TextArea
							handleChange={(e) => setReason(e.target.value)}
							label={t("scoring.reasonForChange")}
							name="reason"
							placeholder={t("scoring.reasonPlaceholder")}
							showError={false}
							value={reason}
						/>
					</div>
				</div>
			</Modal>
		</div>
	);
};
